import { useLocation } from 'react-router-dom';
import { tracking } from '@lib';
import { cn } from '@quickmd-llc/design-system';
import { DevToolsAppContent } from 'features/devtools/DevTools';
import Navigation from './Navigation';
import { POST_ONBOARDING_ROUTES } from '@constants';
import React from 'react';
import { useScroll } from 'react-use';
/* CSS SIZE NOTE
There is a dom border blending bug on windows that can cause the bg under the gradient header to slightly blend with the
header in the last pixel on the sides even though they are technically covered. to account for that, we need to widen
the content container by 2px on each side, and add that amount of padding to the body to essentially have the body the
size we want, but have the header "overflow" that size by 2px on each side so as to avoid the blending issue. tried
using negative margin and caused scrollbar issues on some screens so going with this route. could also possibly just
have the header/gradien be 100vw and just force the content in the nav to be smaller and centered, but not sure if there
are pages with other items on the sides which that would cover or get in the way of and this works
*/
interface Props {
  children: React.ReactNode;
  className?: string;
  isSubPage?: boolean;
  onBackClick?: () => void;
  hideBackButton?: boolean;
  hideProgressBar?: boolean;
  currentPosition?: number;
  navigationSteps?: number;
}

const defaultNavigationSteps = Object.keys(POST_ONBOARDING_ROUTES).length;
const subPageClasses = 'absolute top-0 left-0 right-0 bottom-0 z-40';

export const LAYOUT_HEADER_CONTENT_CONTAINER_ID =
  'layout-header-content-container';

export const LAYOUT_CONTAINER_ID = 'layout-container';

const HEADER_BOTTOM_PADDING = 16;

export default function Layout({
  children,
  className,
  hideBackButton,
  hideProgressBar,
  currentPosition,
  navigationSteps = defaultNavigationSteps,
  // !! This is problematic for the chat widget in that the chat widget is positioned based on IDs that are in the
  // parent layout, and if the parent layout is different (e.g., has a progress bar where the consent form doesn't) the
  // widget will be off when placed on top of the sub page. for now, it works because the only sub page is ConsentForms,
  // which, if i remove the progress bar (which i think is fine and maybe even desirable) then they match up. However,
  // it will break if the sub page is not the same as the main parent page. should consider refactoring and removing
  // isSubPage as it seems to be a potentially problematic pattern in the codebase
  isSubPage,
  onBackClick,
}: Props): JSX.Element {
  const { pathname } = useLocation();

  React.useEffect(() => {
    if (isSubPage) {
      return;
    }

    tracking.trackPage({ pathname });
  }, [isSubPage, pathname]);

  const containerRef = React.useRef<HTMLDivElement>(null);

  // we only want the gradient to show when the content has scrolled putting it under the non gradient portion of the header
  const { y } = useScroll(containerRef);

  return (
    <>
      <div
        ref={containerRef}
        id={LAYOUT_CONTAINER_ID}
        // do not add any top padding/margin here. the header needs to go all the way to the top in order for the
        //see CSS SIZE NOTE above for explanation of the calc -2px
        className={cn(
          'h-dvh overflow-auto bg-app-background px-[calc(16px-2px)] pb-4 md:px-[calc(32px-2px)] md:pb-8 lg:px-[calc(48px-2px)] lg:pb-12',
          { [subPageClasses]: isSubPage },
        )}
      >
        {/* see CSS SIZE NOTE above for explanation of the +4px */}
        <div className="m-auto flex min-h-full w-full flex-col md:w-[calc(512px+4px)] md:max-w-[calc(512px+4px)] lg:w-[640px] lg:max-w-[calc(640px+4px)]">
          <div className="sticky top-0 z-40 bg-app-background">
            <div
              // any desired spacing from header content to the top of the page needs to be added here
              // as for the 'px-[2px]' see the CSS SIZE NOTE above
              className="px-[2px] pt-4 md:pt-8"
              style={{ paddingBottom: HEADER_BOTTOM_PADDING }}
            >
              <div id={LAYOUT_HEADER_CONTENT_CONTAINER_ID}>
                <Navigation
                  onBackClick={onBackClick}
                  hideBackButton={hideBackButton}
                  hideProgressBar={hideProgressBar}
                  currentPosition={currentPosition}
                  navigationSteps={navigationSteps!}
                />
              </div>
            </div>
            <div
              // we want the gradient to sit directly below the header, make sure it does not block click events and only
              // want to show it after the user has scrolled such that the content would overlap/go under the sticky header
              className={cn(
                'pointer-events-none absolute bottom-0 h-6 w-full bg-transparent',
                y ? 'opacity-100' : 'opacity-0',
              )}
            />
          </div>
          {/* see CSS SIZE NOTE above for explanation of the +4px of padding here */}
          <div className="flex flex-1 flex-col px-[2px]">
            <div className={cn('w-full flex-1', className)}>{children}</div>
          </div>
        </div>
      </div>

      <DevToolsAppContent />
    </>
  );
}
